/**
 * Expiration time selection step for custom request flow.
 * Slider-based selection for how long the restaurant has to respond.
 * @module Widget/CustomRequest/steps/ExpirationTimeStep
 */

import React, { useMemo } from 'react';
import type { CustomRequestExpirationOption } from '../../lib/types.ts';
import { FORM_CONSTRAINTS } from '../constants.ts';
import styles from '../CustomRequest.module.css';

interface ExpirationTimeStepProps {
  options: CustomRequestExpirationOption[];
  selectedHours: number | undefined;
  onChange: (hours: number | undefined) => void;
  responseTimeText?: string | null;
  restaurantName?: string;
}

/**
 * Slider selection for custom request expiration window.
 * Filters options to >= 24 hours. Maps slider position to option index.
 *
 * @param props - Component props
 * @returns Rendered expiration time slider
 */
const ExpirationTimeStep: React.FC<ExpirationTimeStepProps> = ({
  options,
  selectedHours,
  onChange,
  responseTimeText,
  restaurantName,
}) => {
  const validOptions = options.filter(
    (opt) => opt.hours >= FORM_CONSTRAINTS.MIN_EXPIRATION_HOURS
  );

  const selectedIndex = useMemo(() => {
    if (selectedHours === undefined) return 0;
    const idx = validOptions.findIndex((o) => o.hours === selectedHours);
    return idx >= 0 ? idx : 0;
  }, [selectedHours, validOptions]);

  const selectedDescription = useMemo(() => {
    if (validOptions.length === 0) return '';
    return validOptions[selectedIndex]?.description ?? '';
  }, [validOptions, selectedIndex]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);
    const option = validOptions[index];
    if (option) {
      onChange(option.hours);
    }
  };

  if (validOptions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className={styles.sectionSubtitle}>
        How long should the restaurant have to respond?
      </p>

      <div className="flex flex-col items-center gap-6 py-8">
        <div className="text-center">
          <div className={styles.sliderValue}>{selectedDescription}</div>
          <div className={styles.sliderLabel}>Expiration time</div>
        </div>

        <div className="w-full px-4">
          <input
            type="range"
            min={0}
            max={validOptions.length - 1}
            step={1}
            value={selectedIndex}
            onChange={handleSliderChange}
            aria-label="Expiration time selection"
            aria-valuetext={selectedDescription}
            className={styles.slider}
          />
        </div>

        {validOptions.length > 1 && (
          <div className="flex w-full justify-between px-4 text-xs text-gray-400"
            style={{ fontFamily: "'Sailec', sans-serif" }}>
            <span>{validOptions[0]?.description}</span>
            <span>{validOptions[validOptions.length - 1]?.description}</span>
          </div>
        )}

        {(responseTimeText || restaurantName) && (
          <p className={styles.sliderHelpText}>
            {responseTimeText ||
              `${restaurantName} will respond to your request as soon as they are able.`}
          </p>
        )}
      </div>

      <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
        <p className="font-['Sailec',sans-serif] text-[13px] leading-relaxed text-gray-500 m-0">
          You can cancel your custom request at any time before the restaurant accepts it.
          If cancelled, the pre-authorization hold on your card will be released and you will not be charged.
        </p>
      </div>
    </div>
  );
};

export default ExpirationTimeStep;
